import type { EventTone } from '@/components/calendar/types';
import type { Translations } from '@/lib/i18n';

import type {
  BusinessVisitDto,
  JobRecurrence,
  JobVisitDto,
  PaymentMethod,
  PaymentStatus,
} from '@/lib/api/jobs';
import type { PaymentMethodDto } from '@/lib/api/payment-methods';

import type { AddJobInput, PaymentMethodValue, RecurrenceInput } from './schema';
import type { FilterState, Job, JobStatus } from './types';

/** The flattened recurrence-rule fields carried on a read `Job`. */
export type JobRecurrenceFields = Pick<
  Job,
  | 'frequency'
  | 'interval_count'
  | 'days_of_week'
  | 'day_of_month'
  | 'custom_rule'
  | 'series_end_date'
>;

// ─── Constants ────────────────────────────────────────────────────────────────

export const PAGE_SIZE = 6;
export const TODAY = new Date().toISOString().slice(0, 10);

// ─── Date utils ───────────────────────────────────────────────────────────────

export function daysFromToday(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export function getWeekRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - now.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

export function getMonthRange(): { start: string; end: string } {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10),
  };
}

export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Parses a bare `YYYY-MM-DD` into a local-midnight Date, sidestepping the
// UTC parsing `new Date('2026-01-01')` would do.
export function dateFromISO(iso: string): Date | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Adds `years` to a bare `YYYY-MM-DD` string — powers the "Year contract"
// end-date mode. Returns '' when `iso` is blank/invalid.
export function addYears(iso: string, years: number): string {
  const date = dateFromISO(iso);
  if (!date) return '';
  date.setFullYear(date.getFullYear() + years);
  return toISODate(date);
}

// Every 30-minute time-of-day slot, in 24h "HH:MM" form — backs the
// start/end time dropdowns so selections are always clean half-hour steps.
export const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

// Formats a "HH:MM" (24h) value as a 12h label, e.g. "13:30" -> "1:30 PM".
export function formatTimeOption(value: string): string {
  const [h, m] = value.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// Adds `minutes` to a "HH:MM" (24h) value, wrapping past midnight.
export function addMinutesToTime(value: string, minutes: number): string {
  const [h, m] = value.split(':').map(Number);
  const total = (h * 60 + m + minutes + 1440) % 1440;
  const h24 = Math.floor(total / 60);
  const m2 = total % 60;
  return `${String(h24).padStart(2, '0')}:${String(m2).padStart(2, '0')}`;
}

/**
 * Best-effort client-side preview of the first `limit` visit dates a
 * recurrence would generate, so the create form can set expectations
 * ("Creates 7 visits: Sep 7, Sep 14, …"). Weekly + monthly are computed
 * locally; `custom` RRULEs return `[]` (no rrule dependency). Monthly days
 * that overflow a short month (e.g. the 31st in Feb) skip that month.
 */
export function previewOccurrenceDates(
  startDate: string,
  rec: RecurrenceInput,
  limit = 8,
): string[] {
  const start = dateFromISO(startDate);
  if (!start) return [];

  const end = rec.seriesEndDate ? dateFromISO(rec.seriesEndDate) : null;
  const parsedInterval = Number(rec.intervalCount);
  const interval =
    Number.isFinite(parsedInterval) && parsedInterval > 0
      ? Math.floor(parsedInterval)
      : 1;

  const within = (d: Date) => d >= start && (!end || d <= end);
  const out: string[] = [];

  if (rec.frequency === 'weekly') {
    const days = [...(rec.daysOfWeek ?? [])].sort((a, b) => a - b);
    if (days.length === 0) return [];

    const cursor = new Date(start);
    cursor.setDate(cursor.getDate() - cursor.getDay()); // Sunday of the start week

    for (let cycle = 0; cycle < 520 && out.length < limit; cycle += 1) {
      for (const day of days) {
        const cand = new Date(cursor);
        cand.setDate(cand.getDate() + day);
        if (within(cand)) {
          out.push(toISODate(cand));
          if (out.length >= limit) break;
        } else if (end && cand > end) {
          return out;
        }
      }
      cursor.setDate(cursor.getDate() + 7 * interval);
      if (end && cursor > end) break;
    }
    return out;
  }

  if (rec.frequency === 'monthly') {
    const dom = Number(rec.dayOfMonth);
    if (!Number.isFinite(dom) || dom < 1 || dom > 31) return [];

    for (let k = 0; k < 120 && out.length < limit; k += 1) {
      const cand = new Date(start.getFullYear(), start.getMonth() + k * interval, dom);
      if (cand.getDate() !== dom) continue; // month too short — skip it
      if (within(cand)) out.push(toISODate(cand));
      else if (end && cand > end) break;
    }
    return out;
  }

  return [];
}

// Builds a display string from start_time/end_time — the backend has no
// separate "duration" field, just optional start/end times.
export function formatTimeRange(job: Job): string {
  if (job.start_time && job.end_time) return `${job.start_time} – ${job.end_time}`;
  if (job.start_time) return job.start_time;
  return '—';
}

// A visit carries its own start/end times, independent of the parent job.
export function formatVisitTimeRange(visit: JobVisitDto): string {
  if (visit.start_time && visit.end_time) {
    return `${visit.start_time} – ${visit.end_time}`;
  }
  if (visit.start_time) return visit.start_time;
  return '—';
}

export function isRecurring(job: Pick<Job, 'frequency'>): boolean {
  return Boolean(job.frequency);
}

export type AssignmentState = 'unknown' | 'unassigned' | 'assigned';

// 'unknown' when the API hasn't sent `assigned_employee_count` yet — the card
// renders no badge and the assignment filter treats it as non-matching. Narrow
// like `isRecurring` so it also works on a `BusinessVisitDto`'s denormalized
// `job_assigned_employee_count` field, not just a full `Job`.
export function assignmentState(
  job: Pick<Job, 'assigned_employee_count'>,
): AssignmentState {
  if (job.assigned_employee_count == null) return 'unknown';
  return job.assigned_employee_count > 0 ? 'assigned' : 'unassigned';
}

// Colour bucket for a visit's status chip — shared by the jobs-page calendar
// (`jobs-calendar.tsx`) and the job-detail visit calendar.
export function visitTone(status: JobStatus): EventTone {
  if (status === 'started') return 'started';
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  return 'scheduled';
}

// Localised label for a visit's status chip — shared by the owner's visit
// row and the employee visit-detail screen.
export function visitStatusLabel(status: JobStatus, t: Translations): string {
  if (status === 'started') return t.inProgress;
  if (status === 'completed') return t.completed;
  if (status === 'cancelled') return t.cancelled;
  return t.scheduled;
}

/**
 * Applies the jobs-page Type/Assignment filters to the business-wide visits
 * feed, using each visit's denormalized parent-job fields — the same
 * semantics as filtering the job list itself (see `isRecurring` /
 * `assignmentState`), just without a second request per visit.
 */
export function filterBusinessVisits(
  visits: BusinessVisitDto[],
  filters: Pick<FilterState, 'type' | 'assignment'>,
): BusinessVisitDto[] {
  let list = visits;

  if (filters.type !== 'all') {
    list = list.filter((v) =>
      filters.type === 'recurring'
        ? isRecurring({ frequency: v.job_frequency })
        : !isRecurring({ frequency: v.job_frequency }),
    );
  }

  if (filters.assignment !== 'all') {
    list = list.filter(
      (v) =>
        assignmentState({ assigned_employee_count: v.job_assigned_employee_count }) ===
        filters.assignment,
    );
  }

  return list;
}

// Maps the Add Job modal's string-y recurrence inputs to the API's
// `JobRecurrence` shape, dropping empty optionals and the fields that don't
// apply to the chosen frequency.
export function toJobRecurrence(input: RecurrenceInput): JobRecurrence {
  const rec: JobRecurrence = { frequency: input.frequency };

  const interval = input.intervalCount ? Number(input.intervalCount) : NaN;
  if (Number.isFinite(interval) && interval > 1) rec.interval_count = interval;

  if (input.frequency === 'weekly' && input.daysOfWeek?.length) {
    rec.days_of_week = input.daysOfWeek;
  }

  if (input.frequency === 'monthly' && input.dayOfMonth) {
    const dom = Number(input.dayOfMonth);
    if (Number.isFinite(dom)) rec.day_of_month = dom;
  }

  if (input.frequency === 'custom' && input.customRule) {
    rec.custom_rule = input.customRule;
  }

  if (input.seriesEndDate) rec.series_end_date = input.seriesEndDate;

  return rec;
}

// Inverse of `toJobRecurrence` — hydrates the recurrence sub-form from a
// job's flattened recurrence fields (or sensible defaults when it has none).
export function fromJobRecurrence(
  job: JobRecurrenceFields | null,
): RecurrenceInput {
  return {
    frequency: job?.frequency ?? 'weekly',
    intervalCount: job?.interval_count != null ? String(job.interval_count) : '1',
    daysOfWeek: job?.days_of_week ?? [],
    dayOfMonth: job?.day_of_month != null ? String(job.day_of_month) : '',
    customRule: job?.custom_rule ?? '',
    seriesEndDate: job?.series_end_date ?? '',
  };
}

// Prefills the Add Job form from an existing job, for the "Copy Job" action.
// Recurrence is never carried over — a copy is always a fresh one-time job
// the user can reschedule (typically by changing the date/time) before saving.
export function jobToAddJobInput(job: Job): AddJobInput {
  return {
    customerMode: 'existing',
    customerUserId: job.customer_user_id,
    newCustomerName: '',
    newCustomerEmail: '',
    newCustomerPhone: '',
    title: job.title,
    description: job.description ?? '',
    serviceAddress: job.service_address ?? '',
    startDate: job.start_date,
    endDate: job.end_date ?? '',
    startTime: job.start_time?.slice(0, 5) ?? '',
    endTime: job.end_time?.slice(0, 5) ?? '',
    totalAmount: job.total_amount != null ? String(job.total_amount) : '',
    defaultPaymentMethod: job.default_payment_method_id ?? job.default_payment_method ?? '',
    repeats: false,
    recurrence: fromJobRecurrence(null),
  };
}

// ─── Payment method ───────────────────────────────────────────────────────────

// Localised label for one payment-method enum value.
export function paymentMethodLabel(
  method: PaymentMethod,
  t: Translations,
): string {
  const labels: Record<PaymentMethod, string> = {
    zelle: t.paymentMethodZelle,
    venmo: t.paymentMethodVenmo,
    stripe: t.paymentMethodStripe,
    cash: t.paymentMethodCash,
    check: t.paymentMethodCheck,
    other: t.paymentMethodOther,
  };
  return labels[method];
}

// One option per *configured account* — not one per type — so two Venmo
// accounts each get their own entry instead of collapsing into a single
// generic "Venmo". `value` is the payment method id when the option is
// backed by a configured account, or the bare type for the synthetic
// `cash`/`check` fallbacks (which need no setup). `canUseStripe` strips
// `stripe` even if a previously-connected account left a configured entry
// behind (e.g. after downgrading off the `stripe_payments` tier feature).
export interface PaymentMethodOption {
  value: string;
  type: PaymentMethodValue;
  id: string | null;
  label: string;
}

export function availablePaymentMethodOptions(
  t: Translations,
  configuredMethods: PaymentMethodDto[],
  canUseStripe: boolean,
): PaymentMethodOption[] {
  const active = configuredMethods.filter(
    (m) => m.is_active && (m.type !== 'stripe' || canUseStripe),
  );
  const options: PaymentMethodOption[] = active.map((m) => ({
    value: m.id,
    type: m.type as PaymentMethodValue,
    id: m.id,
    label: m.label || paymentMethodLabel(m.type as PaymentMethod, t),
  }));
  for (const type of ['cash', 'check'] as const) {
    if (!active.some((m) => m.type === type)) {
      options.push({ value: type, type, id: null, label: paymentMethodLabel(type, t) });
    }
  }
  return options;
}

// Resolves a job/visit's payment method to a display label, preferring the
// specific configured account (by id, e.g. one of several Venmo accounts)
// and falling back to the bare type when there's no id or no matching
// account (e.g. it was later deleted).
export function resolvePaymentMethodLabel(
  t: Translations,
  configuredMethods: PaymentMethodDto[] | undefined,
  type: PaymentMethod | null,
  id: string | null,
): string | null {
  if (id) {
    const match = configuredMethods?.find((m) => m.id === id);
    if (match) return match.label || paymentMethodLabel(match.type as PaymentMethod, t);
  }
  return type ? paymentMethodLabel(type, t) : null;
}

// Resolves a job/visit's stored payment method (type + optional id) to the
// `value` one of `availablePaymentMethodOptions`' own options actually uses,
// so a `<select>` seeded from it lands on a real option instead of an
// orphaned value. Prefers an exact id match, then falls back to the first
// option of the same type (handles data recorded before `..._id` existed, or
// after the specific account was since configured/reconfigured) — cash/check
// always resolve this way since they have no id. Returns `''` (no selection)
// when nothing matches, e.g. the type is `other` or `stripe` with no
// currently configured/active account.
export function paymentMethodOptionValue(
  options: PaymentMethodOption[],
  type: PaymentMethod | null | undefined,
  id: string | null | undefined,
): string {
  if (id) {
    const byId = options.find((o) => o.id === id);
    if (byId) return byId.value;
  }
  if (type) {
    const byType = options.find((o) => o.type === type);
    if (byType) return byType.value;
  }
  return '';
}

// ─── Styling ──────────────────────────────────────────────────────────────────
// Backend job statuses: draft | started | completed | cancelled.

export function statusColorClass(status: JobStatus): string {
  switch (status) {
    case 'started':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'completed':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'cancelled':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }
}

// Payment badge colours — green when settled in full, amber for a partial
// collection, muted for still-unpaid.
export function paymentColorClass(
  status: PaymentStatus,
  isPartial: boolean,
): string {
  if (status === 'paid') {
    return isPartial
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  }
  return 'bg-muted text-muted-foreground';
}

export function todayDotClass(status: JobStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-green-500';
    case 'started':
      return 'bg-blue-500';
    case 'cancelled':
      return 'bg-red-500';
    default:
      return 'bg-amber-400';
  }
}
